/**
 * RIG graph layout — pure geometry.
 *
 * Places the sealed grammar (signal top-center; DIRECT contacts in the
 * signal-company box; DELIVERY hexes on a left spine; PARTICIPANT contacts
 * routed through a consortium waypoint; overflow clusters per side) on an
 * ADAPTIVE, COMPACT coordinate system, then emits a CONTENT-DERIVED viewBox so
 * the graph fills its frame at a usable node scale for any data density — no
 * fixed 900x720 canvas, no letterbox, no vast empty space when populations are
 * small. Rows are pitch-based and centered, so few nodes cluster tightly and
 * many spread without overflowing (the viewBox grows to fit).
 *
 * No DOM here — pure output, so layout invariants are node-testable.
 */

import type { BasisFamily, RigViewModel, Temperature } from "./types";
import { initials, ownerInitial } from "./logic";

export type GNodeKind = "signal" | "contact" | "delivery" | "waypoint" | "cluster";

export interface GNode {
  id: string;
  kind: GNodeKind;
  x: number;
  y: number;
  r: number;
  active: boolean;
  // contact
  name?: string;
  /** Truncated display name for the graph label (full name stays on `name`). */
  label?: string;
  nodeInitials?: string;
  ownerInit?: string;
  temp?: Temperature;
  basis?: BasisFamily;
  // delivery
  practice?: string;
  practiceAbbrev?: string;
  glyph?: string;
  status?: "Active" | "Dormant";
  snippet?: string;
  // cluster
  count?: number;
}

export type GEdgeKind = "direct" | "participant" | "delivery" | "waypoint";
export interface GEdge {
  id: string;
  kind: GEdgeKind;
  d: string;
  active: boolean;
}

/** A soft proximity halo grouping an org's nodes, with a quiet floating label.
 *  Replaces the sealed v1 dashed org-box + pill: no canvas box or pill furniture,
 *  just a low-opacity radial fade behind the cluster and an unboxed label. */
export interface GGroupHalo {
  id: string;
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  /** Display label — middle-truncated so a long compound org name cannot
   *  overrun the node cluster and cross the edge fan / outer halos. */
  label: string;
  /** Full org name for the tooltip (title). */
  labelFull: string;
  labelX: number;
  labelY: number;
}

export interface GWaypointLabel {
  x: number;
  y: number;
  w: number;
  /** Middle-truncated display text (uppercased). */
  text: string;
  /** Full consortium name for the tooltip. */
  full: string;
}

export interface GraphModel {
  viewBox: string;
  width: number;
  height: number;
  signal: GNode;
  directs: GNode[];
  participants: GNode[];
  deliveries: GNode[];
  waypoint: GNode | null;
  waypointLabel: GWaypointLabel | null;
  clusters: GNode[];
  edges: GEdge[];
  groups: GGroupHalo[];
}

// --- Node sizing (RIG-DENSITY-1: temperature → size, bounded). Spheres run a
//     touch larger than the sealed v1 flat circles so the neumorphic material
//     reads at the new generous-air spacing. ---
const CONTACT_R_BY_TEMP: Record<Temperature, number> = {
  hot: 36,
  warm: 32,
  cooling: 29,
  cold: 26,
};
export const MIN_CONTACT_R = 26;
export const MAX_CONTACT_R = 36;
export function contactRadius(temp: Temperature): number {
  return CONTACT_R_BY_TEMP[temp];
}

const DELIVERY_R = 33;
const CLUSTER_R = 28;
const WAY_R = 24;

// --- Compact coordinate bands. The main relationship column runs down CX;
//     deliveries hang off a left spur. Absolute values are nominal — the final
//     viewBox is derived from the content, so only the relative geometry
//     matters. Bands are close enough to read as one composed story. ---
const CX = 380;
const SIGNAL_W = 250;
const SIGNAL_H = 82;
const SIGNAL_CY = 52;
const SIGNAL_TOP = SIGNAL_CY - SIGNAL_H / 2;
const SIGNAL_BOTTOM = SIGNAL_CY + SIGNAL_H / 2;

// Generous air (v2 aesthetic): the signal→waypoint→participant travel breathes
// so the organic curved edges read as relationships, not corridors, and the soft
// proximity halos have room to sit behind each org's nodes without touching.
const CONTACT_PITCH = 112;
const DIRECT_Y = 190;
const WAY_CY = 296;
const PART_Y = 452;

const DELIVERY_X = 96;
const DELIVERY_TOP = 184;
const DELIVERY_PITCH = 100;

const VIEW_PAD = 24;

// Name labels (item 1): widen a row's pitch to fit its longest (truncated) name
// so labels never touch; end-ellipsis very long names (full name in a tooltip).
const NAME_CHAR_W = 6.9;
const NAME_MAX_CHARS = 18;
const NAME_LABEL_GAP = 18;

// Org group labels (item 3A.3): a long compound org name (e.g. "QATAR
// INVESTMENT AUTHORITY (QIA) & BROOKFIELD") centred over its cluster overruns
// the outer nodes and crosses the fanned edges / outer halos. Middle-truncate
// to keep the label within the cluster (full name stays in the tooltip); the
// short sealed names (NATIONAL GRID, BLACKROCK) are unaffected.
const ORG_LABEL_MAX = 24;

/** End-ellipsis truncation. */
export function truncateEnd(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
/** Middle-ellipsis truncation, preserving both ends of a compound name. */
export function truncateMiddle(s: string, max: number): string {
  if (s.length <= max) return s;
  const head = Math.ceil((max - 1) / 2);
  const tail = Math.floor((max - 1) / 2);
  return `${s.slice(0, head)}…${s.slice(s.length - tail)}`;
}
function nameLabelWidth(name: string): number {
  return truncateEnd(name, NAME_MAX_CHARS).length * NAME_CHAR_W;
}
/** Row pitch that keeps adjacent name labels from touching. */
function rowPitch(names: string[]): number {
  const maxW = names.length > 0 ? Math.max(...names.map(nameLabelWidth)) : 0;
  return Math.max(CONTACT_PITCH, maxW + NAME_LABEL_GAP);
}

/** Smooth cubic path between two points with a vertical S-bias. */
function curve(x1: number, y1: number, x2: number, y2: number): string {
  const c1x = x1 + (x2 - x1) * 0.25;
  const c1y = y1 + (y2 - y1) * 0.55;
  const c2x = x1 + (x2 - x1) * 0.75;
  const c2y = y1 + (y2 - y1) * 0.78;
  return `M${x1.toFixed(0)},${y1.toFixed(0)} C${c1x.toFixed(0)},${c1y.toFixed(0)} ${c2x.toFixed(0)},${c2y.toFixed(0)} ${x2.toFixed(0)},${y2.toFixed(0)}`;
}

/** Smooth cubic path with a horizontal S-bias — for the delivery spur, which
 *  runs mostly sideways off the account rather than straight down. */
function curveH(x1: number, y1: number, x2: number, y2: number): string {
  const mx = x1 + (x2 - x1) * 0.5;
  return `M${x1.toFixed(0)},${y1.toFixed(0)} C${mx.toFixed(0)},${y1.toFixed(0)} ${mx.toFixed(0)},${y2.toFixed(0)} ${x2.toFixed(0)},${y2.toFixed(0)}`;
}

/** Build a soft proximity halo (+ quiet unboxed label above) around a group of
 *  nodes — the v2 replacement for the dashed org-box + pill. */
function groupHalo(id: string, nodes: GNode[], label: string): GGroupHalo {
  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const rMax = Math.max(...nodes.map((n) => n.r));
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  return {
    id,
    cx,
    cy,
    rx: (maxX - minX) / 2 + rMax + 40,
    ry: (maxY - minY) / 2 + rMax + 34,
    label: truncateMiddle(label, ORG_LABEL_MAX),
    labelFull: label,
    labelX: cx,
    labelY: minY - rMax - 14,
  };
}

/** Pitch-based centered row: n items spaced `pitch` apart, centered on cx. */
function rowXs(cx: number, n: number, pitch: number): number[] {
  if (n <= 0) return [];
  const start = cx - ((n - 1) * pitch) / 2;
  return Array.from({ length: n }, (_, i) => start + i * pitch);
}

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}
function emptyBounds(): Bounds {
  return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}
function extend(b: Bounds, x1: number, y1: number, x2: number, y2: number): void {
  b.minX = Math.min(b.minX, x1);
  b.minY = Math.min(b.minY, y1);
  b.maxX = Math.max(b.maxX, x2);
  b.maxY = Math.max(b.maxY, y2);
}

export function layoutGraph(vm: RigViewModel): GraphModel {
  const directsIn = vm.contacts.filter((c) => c.basis === "direct");
  const participantsIn = vm.contacts.filter((c) => c.basis === "participant");
  const directClusterData = vm.clusters.find((c) => c.basis === "direct") ?? null;
  const participantClusterData = vm.clusters.find((c) => c.basis === "participant") ?? null;
  const hasParticipants = participantsIn.length > 0 || participantClusterData != null;

  const edges: GEdge[] = [];
  const groups: GGroupHalo[] = [];
  const clusters: GNode[] = [];

  const signal: GNode = {
    id: "signal",
    kind: "signal",
    x: CX,
    y: SIGNAL_CY,
    r: 0,
    active: true,
  };

  // --- DIRECT contacts: row under the signal, solid edges. ---
  const directHasCluster = directClusterData != null && directClusterData.count > 0;
  const directCount = directsIn.length + (directHasCluster ? 1 : 0);
  const directSlots = rowXs(CX, directCount, rowPitch(directsIn.map((c) => c.name)));
  const directs: GNode[] = directsIn.map((c, i) => ({
    id: c.id,
    kind: "contact",
    x: directSlots[i] ?? CX,
    y: DIRECT_Y,
    r: contactRadius(c.temp),
    active: c.active,
    name: c.name,
    label: truncateEnd(c.name, NAME_MAX_CHARS),
    nodeInitials: initials(c.name),
    ownerInit: ownerInitial(c.owner),
    temp: c.temp,
    basis: "direct",
  }));
  directs.forEach((n) => {
    edges.push({
      id: `e-direct-${n.id}`,
      kind: "direct",
      d: curve(CX - 24, SIGNAL_BOTTOM, n.x, n.y - n.r),
      active: n.active,
    });
  });

  let directClusterNode: GNode | null = null;
  if (directHasCluster && directClusterData) {
    directClusterNode = {
      id: "cluster-direct",
      kind: "cluster",
      x: directSlots[directCount - 1] ?? CX,
      y: DIRECT_Y,
      r: CLUSTER_R,
      active: false,
      count: directClusterData.count,
    };
    clusters.push(directClusterNode);
    edges.push({
      id: "e-cluster-direct",
      kind: "direct",
      d: curve(CX - 24, SIGNAL_BOTTOM, directClusterNode.x, directClusterNode.y - directClusterNode.r),
      active: false,
    });
  }

  // Signal-company proximity halo around the direct row (+ its cluster).
  const signalGroupNodes: GNode[] = [...directs, ...(directClusterNode ? [directClusterNode] : [])];
  if (signalGroupNodes.length > 0) {
    groups.push(
      groupHalo(
        "org-signal",
        signalGroupNodes,
        (vm.orgs.find((o) => o.role === "signal")?.name ?? "").toUpperCase(),
      ),
    );
  }

  // --- DELIVERY hexes: left column on a spine off the signal company. ---
  const deliveries: GNode[] = vm.deliveries.map((d, i) => ({
    id: `d${i + 1}`,
    kind: "delivery",
    x: DELIVERY_X,
    y: DELIVERY_TOP + i * DELIVERY_PITCH,
    r: DELIVERY_R,
    active: d.status === "Active",
    practice: d.practice,
    practiceAbbrev: d.practiceAbbrev,
    glyph: d.caseStudy ? "✓" : "—",
    status: d.status,
    snippet: d.snippet,
  }));
  if (deliveries.length > 0) {
    // Delivery hangs off the account: a horizontal-biased curve from the signal
    // node's lower-left toward each hex's right edge (no elbow, no value chip —
    // the tier lives in the delivery read, off-canvas).
    const anchorX = CX - SIGNAL_W / 2 + 12;
    const anchorY = SIGNAL_BOTTOM;
    deliveries.forEach((n) => {
      edges.push({
        id: `e-delivery-${n.id}`,
        kind: "delivery",
        d: curveH(anchorX, anchorY, n.x + n.r, n.y),
        active: n.active,
      });
    });
  }

  // --- WAYPOINT + PARTICIPANT contacts + participant cluster. ---
  let waypoint: GNode | null = null;
  let waypointLabel: GWaypointLabel | null = null;
  const participants: GNode[] = [];

  if (hasParticipants) {
    waypoint = { id: "way", kind: "waypoint", x: CX, y: WAY_CY, r: WAY_R, active: false };
    const wname = vm.waypoint?.name ?? "Consortium";
    const wdisplay = truncateMiddle(wname, 42);
    const labelW = Math.min(320, wdisplay.length * 6.2 + 24);
    waypointLabel = {
      x: CX - labelW / 2,
      y: WAY_CY + WAY_R + 12,
      w: labelW,
      text: wdisplay.toUpperCase(),
      full: wname,
    };
    edges.push({
      id: "e-way",
      kind: "waypoint",
      d: curve(CX + 20, SIGNAL_BOTTOM, CX, WAY_CY - WAY_R),
      active: false,
    });

    const partHasCluster = participantClusterData != null && participantClusterData.count > 0;
    const partCount = participantsIn.length + (partHasCluster ? 1 : 0);
    const partSlots = rowXs(CX, partCount, rowPitch(participantsIn.map((c) => c.name)));
    participantsIn.forEach((c, i) => {
      const node: GNode = {
        id: c.id,
        kind: "contact",
        x: partSlots[i] ?? CX,
        y: PART_Y,
        r: contactRadius(c.temp),
        active: c.active,
        name: c.name,
        label: truncateEnd(c.name, NAME_MAX_CHARS),
        nodeInitials: initials(c.name),
        ownerInit: ownerInitial(c.owner),
        temp: c.temp,
        basis: "participant",
      };
      participants.push(node);
      edges.push({
        id: `e-part-${node.id}`,
        kind: "participant",
        d: curve(CX, WAY_CY + WAY_R, node.x, node.y - node.r),
        active: node.active,
      });
    });

    let participantClusterNode: GNode | null = null;
    if (partHasCluster && participantClusterData) {
      participantClusterNode = {
        id: "cluster-participant",
        kind: "cluster",
        x: partSlots[partCount - 1] ?? CX,
        y: PART_Y,
        r: CLUSTER_R,
        active: false,
        count: participantClusterData.count,
      };
      clusters.push(participantClusterNode);
      edges.push({
        id: "e-cluster-participant",
        kind: "participant",
        d: curve(CX, WAY_CY + WAY_R, participantClusterNode.x, participantClusterNode.y - participantClusterNode.r),
        active: false,
      });
    }

    const participantGroupNodes: GNode[] = [
      ...participants,
      ...(participantClusterNode ? [participantClusterNode] : []),
    ];
    if (participantGroupNodes.length > 0) {
      const pname = (participantClusterData?.company ??
        participantsIn[0]?.company ??
        vm.orgs.find((o) => o.role === "participant")?.name ??
        "").toUpperCase();
      groups.push(groupHalo("org-participant", participantGroupNodes, pname));
    }
  }

  // --- Content-derived viewBox: union of every drawn element (+ per-node
  //     padding for name labels, owner badges, delivery captions, org-box
  //     labels), then a small margin. Fills the frame at any density. ---
  const b = emptyBounds();
  extend(b, CX - SIGNAL_W / 2, SIGNAL_TOP, CX + SIGNAL_W / 2, SIGNAL_BOTTOM);
  for (const n of [...directs, ...participants]) {
    // owner badge upper-right (+34,-34); name label below (~ +26 past radius)
    extend(b, n.x - n.r, n.y - n.r - 12, n.x + n.r + 12, n.y + n.r + 28);
  }
  for (const n of deliveries) {
    extend(b, n.x - n.r, n.y - n.r - 10, n.x + n.r, n.y + n.r + 8);
  }
  for (const n of clusters) {
    extend(b, n.x - n.r, n.y - n.r, n.x + n.r, n.y + n.r + 6);
  }
  if (waypoint) extend(b, waypoint.x - WAY_R, waypoint.y - WAY_R, waypoint.x + WAY_R, waypoint.y + WAY_R);
  if (waypointLabel) {
    extend(b, waypointLabel.x, waypointLabel.y, waypointLabel.x + waypointLabel.w, waypointLabel.y + 22);
  }
  for (const g of groups) {
    // the quiet unboxed label (est. width) + the halo's vertical reach; the soft
    // horizontal fade may run slightly past the frame and clips gracefully.
    const lw = g.label.length * 6.5;
    extend(b, g.labelX - lw / 2, g.labelY - 10, g.labelX + lw / 2, g.labelY + 2);
    extend(b, g.cx - g.rx * 0.7, g.cy - g.ry, g.cx + g.rx * 0.7, g.cy + g.ry);
  }

  const vbX = b.minX - VIEW_PAD;
  const vbY = b.minY - VIEW_PAD;
  const vbW = b.maxX - b.minX + VIEW_PAD * 2;
  const vbH = b.maxY - b.minY + VIEW_PAD * 2;

  // SPARSE-GRAPH SCALE CLAMP (3B.5). When ONLY the signal node is drawn (no
  // matched contacts, no cross-practice deliveries, no overflow clusters, no
  // consortium waypoint), the tiny content-derived viewBox (~298×130) scales up
  // to fill the full-width hero, rendering the lone signal node enormously. Floor
  // the viewBox to a minimum extent, expanding symmetrically about the content
  // centre, so the effective node scale is capped and the sparse graph renders
  // proportionately. Gated on a categorical sparse predicate: any graph with ≥1
  // content node keeps the exact content-derived viewBox, so dense and lean
  // graphs are a no-op by construction. The floor is additionally a Math.max
  // no-op for any viewBox already wider/taller than the minimum, so even the
  // predicate firing could not shrink a populated graph's nodes.
  const contentNodeCount =
    directs.length +
    participants.length +
    deliveries.length +
    clusters.length +
    (waypoint ? 1 : 0);
  let outX = vbX;
  let outY = vbY;
  let outW = vbW;
  let outH = vbH;
  if (contentNodeCount === 0) {
    const SPARSE_MIN_VBW = 720;
    const SPARSE_MIN_VBH = 300;
    if (outW < SPARSE_MIN_VBW) {
      const cx = vbX + vbW / 2;
      outW = SPARSE_MIN_VBW;
      outX = cx - outW / 2;
    }
    if (outH < SPARSE_MIN_VBH) {
      const cy = vbY + vbH / 2;
      outH = SPARSE_MIN_VBH;
      outY = cy - outH / 2;
    }
  }

  return {
    viewBox: `${outX.toFixed(1)} ${outY.toFixed(1)} ${outW.toFixed(1)} ${outH.toFixed(1)}`,
    width: outW,
    height: outH,
    signal,
    directs,
    participants,
    deliveries,
    waypoint,
    waypointLabel,
    clusters,
    edges,
    groups,
  };
}

/** Hexagon points string centered at (cx,cy) with circumradius r. */
export function hexPoints(cx: number, cy: number, r: number): string {
  const h = r * 0.866;
  return `${cx - r},${cy} ${cx - r / 2},${cy - h} ${cx + r / 2},${cy - h} ${cx + r},${cy} ${cx + r / 2},${cy + h} ${cx - r / 2},${cy + h}`;
}

/** Diamond points string centered at (cx,cy) with half-extent r. */
export function diamondPoints(cx: number, cy: number, r: number): string {
  return `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
}
